import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router-dom'

import { db } from '../../db/db'
import {
  basculerInterruption,
  choisirDebut,
  choisirFin,
  listerAnomalies,
  lireReglages,
  supprimerNuit,
} from '../../db/repo'
import { formatJour, relatif } from '../../domain/time'
import { SOURCE_LABELS } from '../../domain/types'
import { Carte, Encart, Entete, IconeAlerte } from '../composants'
import { ChoixCandidat, DerouleNuit, MesuresNuit } from '../DerouleNuit'

export function PageAujourdhui() {
  // Une seule requete pour tout l'ecran : trois `useLiveQuery` separes
  // afficheraient trois etats de chargement decorreles au demarrage.
  const donnees = useLiveQuery(async () => {
    const nuits = await db.nuits.toArray()
    nuits.sort((a, b) => b.date.localeCompare(a.date))
    return {
      nuit: nuits[0],
      total: nuits.length,
      reglages: await lireReglages(),
      anomalies: await listerAnomalies(),
    }
  }, [])

  if (!donnees) {
    return (
      <>
        <Entete titre="Cette nuit" />
        <main className="contenu">
          <p className="doux">Chargement…</p>
        </main>
      </>
    )
  }

  const { nuit, reglages, anomalies } = donnees

  return (
    <>
      <Entete
        titre={nuit ? formatJour(nuit.date) : 'Cette nuit'}
        sous={nuit ? SOURCE_LABELS[nuit.source] : undefined}
      />

      <main className="contenu">
        {anomalies.length > 0 && (
          <Encart ton="alerte">
            <div className="ligne ligne--haut">
              <IconeAlerte />
              <div className="grandir">
                <strong>
                  {anomalies.length === 1
                    ? '1 nuit n’a pas pu être formée'
                    : `${anomalies.length} nuits n’ont pas pu être formées`}
                </strong>
                <p style={{ marginTop: 4 }}>{anomalies[0]?.message}</p>
                <p style={{ marginTop: 6 }}>
                  <Link to={`/nuits/nouvelle?date=${anomalies[0]?.date ?? ''}`}>
                    Saisir cette nuit à la main
                  </Link>
                </p>
              </div>
            </div>
          </Encart>
        )}

        {!nuit ? (
          <Carte titre="Aucune nuit enregistrée">
            <p className="doux">
              L’application ne peut rien mesurer seule : iOS n’autorise aucun code à
              s’exécuter en arrière-plan. Ce sont les automatisations Raccourcis qui
              relèvent les heures, et il faut les créer une fois.
            </p>
            <p style={{ marginTop: 12 }}>
              <Link className="btn btn--principal btn--bloc" to="/installation">
                Configurer les raccourcis
              </Link>
            </p>
          </Carte>
        ) : (
          <>
            <Carte>
              <MesuresNuit nuit={nuit} objectifMinutes={reglages.objectifMinutes} />
            </Carte>

            <Carte
              titre="Déroulé"
              action={
                nuit.corrigee ? (
                  <button
                    type="button"
                    className="btn btn--fantome btn--petit"
                    onClick={() => void supprimerNuit(nuit.date)}
                    title="Revenir au découpage automatique"
                  >
                    Annuler mes corrections
                  </button>
                ) : undefined
              }
            >
              <DerouleNuit
                nuit={nuit}
                onBasculer={(index) => void basculerInterruption(nuit.date, index)}
              />
              {nuit.interruptions.length === 0 && (
                <p className="tres-doux" style={{ marginTop: 10 }}>
                  Aucun usage du téléphone détecté entre le coucher et le lever. Un réveil
                  sans toucher au téléphone reste invisible : ce compte est un plancher.
                </p>
              )}
            </Carte>

            {(nuit.candidatsDebut.length > 1 || nuit.candidatsFin.length > 1) && (
              <Carte titre="Autres signaux captés">
                <div className="pile">
                  <ChoixCandidat
                    titre="Heure de coucher"
                    candidats={nuit.candidatsDebut}
                    retenu={nuit.debut}
                    onChoisir={(c) => void choisirDebut(nuit.date, c.at, c.type)}
                  />
                  <ChoixCandidat
                    titre="Heure de lever"
                    candidats={nuit.candidatsFin}
                    retenu={nuit.fin}
                    onChoisir={(c) => void choisirFin(nuit.date, c.at, c.type)}
                  />
                </div>
              </Carte>
            )}

            <p className="tres-doux centre">
              Dernière modification {relatif(nuit.modifieLe)}
              {nuit.corrigee && ' · corrigée à la main, plus recalculée'}
            </p>
          </>
        )}
      </main>
    </>
  )
}
